/**
 * Credits API (lab-credit L3 / ADDENDUM-3) — 1:1 with the FROZEN §2.8/§2.9 API
 * CONTRACT (state/epics/IPD-MVP/super-mvp/BUILD-PLAN-day1.md). Both routes are
 * module-agnostic (source_module today is "LAB"; PHARMACY etc. later with zero
 * rework) and mount under the org's gated admission router. Money is integer
 * paise; errors are `detail:{code,message,...extras}`.
 *
 * Each function returns `{ success, data?, error?, code?, ...extras }` so the
 * caller can branch on the machine-readable `code` (e.g. 409
 * CREDIT_ALREADY_SETTLED → re-read instead of a generic failure banner).
 */
import apiClient from './apiClient';

const BASE = '/api/organisation/credits';

/** Same detail-shape translation the admission charges/payments API uses. */
function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, message, ...extras } = detail;
    return { error: message || fallback, code, ...extras };
  }
  if (typeof detail === 'string') return { error: detail };
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to view or settle credit.' };
    case 404: return { error: 'That credit entry could not be found.', code: 'CREDIT_ENTRY_NOT_FOUND' };
    case 422: return { error: fallback, code: 'VALIDATION_ERROR' };
    default:  return { error: fallback };
  }
}

/**
 * §2.8 GET /credits — the outstanding-credit reader (module-agnostic).
 * `status` defaults to "open" server-side; filters are all optional so the
 * SAME function serves the per-admission Settle card (admission_id), a
 * future per-patient view (patient_id), and a future org-wide report (module).
 */
export const listOutstandingCredits = async ({ patientId, admissionId, module, status } = {}) => {
  try {
    const params = {};
    if (patientId) params.patient_id = patientId;
    if (admissionId) params.admission_id = admissionId;
    if (module) params.module = module;
    if (status) params.status = status;
    const res = await apiClient.get(BASE, { params });
    return {
      success: true,
      data: {
        entries: Array.isArray(res.data?.entries) ? res.data.entries : [],
        count: res.data?.count ?? 0,
        totalOutstandingPaise: res.data?.total_outstanding_paise ?? 0,
      },
    };
  } catch (error) {
    console.error('[CreditsAPI] list error:', error);
    return { success: false, ...parseError(error, 'Failed to load outstanding credit.') };
  }
};

/**
 * §2.9 POST /credits/settle — settle one or more OPEN credit entries on ONE
 * shared receipt. The server re-sums the amount from the entries (client
 * never sends a total); `settlement_date` is optional (Day-1: server FORCES
 * it to org-today regardless — no backdating this phase). A 201 (settled) and
 * a 200 (idempotent replay) are BOTH success.
 *
 * DIAG-DISCOUNT v5 (build-plan-v5.md §1.3): the settle request carries NO
 * waiver input at all — an admin records a waiver separately
 * (`diagWaiverApi.recordDiagnosticWaiver`) and the SERVER allocates it
 * pro-rata across the settled lines. A present, non-empty `waivers` is
 * REFUSED server-side with 422 `WAIVER_LINES_NOT_ACCEPTED` (never silently
 * ignored), so this function must never send that field.
 */
export const settleCredits = async ({
  creditEntryIds, paymentMode, referenceNumber, idempotencyKey,
}) => {
  try {
    const body = {
      credit_entry_ids: creditEntryIds,
      payment_mode: paymentMode,
      idempotency_key: idempotencyKey,
    };
    if (referenceNumber) body.reference_number = referenceNumber;

    const res = await apiClient.post(`${BASE}/settle`, body);
    return {
      success: true,
      data: {
        settled: Array.isArray(res.data?.settled) ? res.data.settled : [],
        payments: Array.isArray(res.data?.payments) ? res.data.payments : [],
        sharedReceiptNumber: res.data?.shared_receipt_number ?? null,
        receiptFy: res.data?.receipt_fy ?? null,
        totalPaise: res.data?.total_paise ?? 0,
        // `null` is a real value (closed-replay path): NEVER coerce it to 0,
        // that would silently lie about a waiver.
        totalWaiverPaise: res.data?.total_waiver_paise ?? null,
        totalNetPaise: res.data?.total_net_paise ?? null,
        // NEW per v5 §1.3 — `waiver_applied_paise` (cumulative applied on the
        // stay to date) / `waiver_remaining_paise` (still unused). Both
        // `null` on the closed-replay path, for the same reason the totals
        // above are; never coerced to 0.
        waiverAppliedPaise: res.data?.waiver_applied_paise ?? null,
        waiverRemainingPaise: res.data?.waiver_remaining_paise ?? null,
        replayed: !!res.data?.replayed,
      },
    };
  } catch (error) {
    console.error('[CreditsAPI] settle error:', error);
    return { success: false, ...parseError(error, 'Failed to settle the selected credit.') };
  }
};

/** New idempotency key per settle click → a double-click settles ONE receipt (replay). */
export function newCreditSettleIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `credit-settle:${crypto.randomUUID()}`;
  return `credit-settle:${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const creditsApi = { listOutstandingCredits, settleCredits, newCreditSettleIdempotencyKey };
export default creditsApi;
